import React from 'react'
import { FormattedMessage } from 'react-intl'
import { parseISO, format } from 'date-fns'
import { de } from 'date-fns/locale'

import { deleteOrder, IOrder } from '@services/shoppingcardservice'

import {
  Button,
  Grid,
  Typography,
  Card,
  CardContent,
  CardActions,
  Toolbar,
  Paper,
} from '@mui/material'
import useDialog from '@hooks/useDialog'
import { styled } from '@mui/system'

const Info = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  lineHeight: '1.4',
  color: theme.palette.primary.contrastText,
}))

export type OrderCardProps = AT.IReactPropsDefaults & {
  onActionClick?: () => void
  order: IOrder
}

export default function OrderCard({ onActionClick, order }: OrderCardProps) {
  const { toggle } = useDialog()

  function formatDate(date: string, formatStr: string) {
    return format(parseISO(date), formatStr, {
      locale: de,
    })
  }
  function renderOrderCards() {
    return order.products.map((product) => (
      <Paper
        key={product.title}
        sx={{
          p: 6,
          mb: 2,
        }}
        elevation={12}
      >
        <Grid container spacing={2}>
          <Grid item>{product.amount}x</Grid>
          <Grid item>{product.title} {product.sku} </Grid>
        </Grid>
      </Paper>
    ))
  }

  return (
    <Card
      elevation={3}
      sx={{
        my: 12,
        mx: 0,
        width: '100%',
      }}
    >
      <Toolbar
        sx={{
          backgroundColor: 'primary.main',
          p: 4,
        }}
      >
        <Grid
          container
          direction="row"
          spacing={1}
          wrap="nowrap"
          justifyContent="space-between"
          alignContent="flex-end"
        >
          <Grid item container direction="column" xs={8} spacing={0}>
            <Grid item container spacing={2}>
              <Grid item>
                <Info>{order.firstname}</Info>
              </Grid>
              <Grid item>
                <Info>{order.lastname}</Info>
              </Grid>
            </Grid>
            {!!order.department && (
              <Grid item>
                <Info>{order.department}</Info>
              </Grid>
            )}
            {!!order.phone && (
              <Grid item>
                <Info>{order.phone}</Info>
              </Grid>
            )}
            <Grid item>
              <Info>{order.email}</Info>
            </Grid>
          </Grid>
          <Grid item container direction="column" alignItems="flex-end">
            {!!order.company && (
              <Grid item>
                <Info>{order.company}</Info>
              </Grid>
            )}
            <Grid item>
              <Info>{order.streetAndNumberOrPoBox}</Info>
            </Grid>
            <Grid item>
              <Info>{order.postcodeAndCity}</Info>
            </Grid>
          </Grid>
          <Grid item container direction="column" alignItems="flex-end">
            <Grid item>
              <Info>
                Angefragt am{' '}
                {formatDate(order.timestamp, "dd.MM.yyyy 'um' HH:mm 'Uhr'")}
              </Info>
            </Grid>
          </Grid>
        </Grid>
      </Toolbar>
      <CardContent>
        <Grid
          sx={{
            mt: 2,
          }}
          container
          wrap="nowrap"
          spacing={2}
          direction="column"
        >
          {!!order.message && (
            <Typography
              sx={{
                my: 4,
                mx: 2,
              }}
              dangerouslySetInnerHTML={{ __html: order.message }}
            />
          )}
          {renderOrderCards()}
        </Grid>
      </CardContent>
      <CardActions>
        <Grid container justifyContent="flex-end" spacing={2}>
          <Grid item>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                toggle('areyousure', () =>
                  deleteOrder(order.id).then(() => {
                    if (onActionClick) onActionClick()
                  })
                )
              }}
            >
              <FormattedMessage id="button.delete" />
            </Button>
          </Grid>
        </Grid>
      </CardActions>
    </Card>
  )
}
